import React from 'react';
import { addLoginValue } from 'app/blocks/analytics/storage';
import { ID, withCodes2, lAsync, l as localizer } from 'app/blocks/common/codes';
import { processing } from 'app/blocks/common/decorators';
import showDialog from 'app/blocks/common/jsx/dialogModal';
import ProfileUrlProvider from 'app/blocks/common/providers/ProfileUrlProvider';
import withResponsive from 'app/blocks/common/responsive-decorator';
import * as Utils from 'app/blocks/common/utils';
import { compose, getQueryParamSafe } from 'app/blocks/common/utils';
import withAuth from 'app/blocks/common/withAuth';
import { confirmLogout } from 'app/blocks/common/withConfirmLeaving';
import LinkButtonOrLink from 'app/blocks/menu/LinkButtonOrLink';
import MobileMenu from 'app/blocks/menu/mobile-menu/mobile-menu';
import SkipLink from 'app/blocks/menu/skip-link/skip-link';
import UserName from 'app/blocks/menu/user-name/user-name';
import { getUiConnectUrl, logout, securityKey } from 'app/blocks/middleware/authentication';
import { getDashboardUrl } from 'app/blocks/middleware/dashboard';
import { getUrlParam } from 'app/blocks/middleware/url-params';
import routes from 'app/pages/routes.json';
import { navigate } from 'app/route/history';
import ResourcesMenu from './resources-menu/resources';

import './menu.scss';

export const handleLoginAction = async () => {
    addLoginValue('page-header');
    const returnUrl = getUrlParam('returnUrl');
    if (returnUrl) {
        navigate('login');
    } else {
        try {
            const url = await getUiConnectUrl(window.location.pathname + window.location.search, false);
            const securityUrl = securityKey(url);
            Utils.windowLocationReplace(securityUrl);
        } catch (error) {
            showDialog.confirmation({
                confirmBtnLabel: localizer('BUTTONS.RELOAD_PAGE'),
                message: localizer('ERROR.OTHER_ERROR'),
                onApprove: () => handleLoginAction(),
            });
        }
    }
};

type State = {
    isUserNameOpen: boolean;
    isResourcesOpen: boolean;
    dashboardURL?: string;
};

type Props = {
    isAuth: boolean;
    isMobile: boolean;
    isDesktop: boolean;
    isTablet: boolean;
    isLargeDesktop: boolean;
    onDisplay?: () => void;
    user?: User;
};

class Menu extends React.Component<Props, State> {
    static defaultProps = {
        onDisplay: () => {},
    };

    state: State = {
        isResourcesOpen: false,
        isUserNameOpen: false,
    };

    // eslint-disable-next-line consistent-return
    async componentDidMount() {
        this.props.onDisplay();
        await this.getAppProperties();

        const queryParams = getQueryParamSafe(window.location.href);
        if (queryParams?.error === 'consent_required') {
            const message = await lAsync('ERROR.CONNECT_TERMSOFSERVICE_ERROR');
            showDialog.info({ message });
        }
    }

    @processing('isPropertiesLoading')
    async getAppProperties() {
        const dashboardURL = await getDashboardUrl();
        this.setState({ dashboardURL });
    }

    onLogout = async () => {
        if (await confirmLogout()) {
            logout();
        }
    };

    hideMenu = () => {
        this.setState({ isResourcesOpen: false, isUserNameOpen: false });
    };

    showUserNameMenu = () => {
        const { isUserNameOpen } = this.state;
        this.setState({ isResourcesOpen: false, isUserNameOpen: !isUserNameOpen });
    };

    showResourcesMenu = () => {
        const { isResourcesOpen } = this.state;
        this.setState({ isResourcesOpen: !isResourcesOpen, isUserNameOpen: false });
    };

    goToLogin = async () => {
        await handleLoginAction();
    };

    render() {
        const { isAuth, isDesktop, isLargeDesktop, isMobile, isTablet, user } = this.props;
        const { dashboardURL, isResourcesOpen, isUserNameOpen } = this.state;
        const register_token = window.location.href.match(/index.html\/?register-invite/);

        return register_token ? (
            <div />
        ) : (
            <ProfileUrlProvider>
                <SkipLink mainContentSelector="#as-main-content-7f3d9e2a" />
                <div className="menu">
                    <nav className="menu-header">
                        <div className="menu-container container-lg">
                            <a
                                aria-label="Wiley"
                                className="menu-logoLink"
                                data-seleniumid="menu-logo-link"
                                href={localizer(`${ID.BUTTONS}.TOP_MENU.LOGO_URL`)}
                            >
                                <div className="menu-logo" data-seleniumid="menu-logo">
                                    <img alt="Wiley" src={localizer(`${ID.BUTTONS}.TOP_MENU.LOGO_BLACK_IMG_URL`)} />
                                </div>
                                <div className="menu-authors" data-seleniumid="menu-authors">
                                    Authors
                                </div>
                            </a>

                            <div className="menu-spacer" />

                            {(isMobile || isTablet) && (
                                <MobileMenu
                                    dashboardURL={dashboardURL}
                                    goToLogin={this.goToLogin}
                                    isAuth={isAuth}
                                    onLogout={this.onLogout}
                                />
                            )}

                            {(isDesktop || isLargeDesktop) && (
                                <UserName
                                    goToLogin={this.goToLogin}
                                    isAuth={isAuth}
                                    isOpen={isUserNameOpen}
                                    onClick={this.showUserNameMenu}
                                    onClose={this.hideMenu}
                                    onLogout={this.onLogout}
                                    user={user}
                                />
                            )}
                        </div>
                    </nav>
                    {(isDesktop || isLargeDesktop) && (
                        <div className="menu-items" data-seleniumid="menu-items" role="menubar">
                            <div className="menu-container container-lg sub-Header">
                                <LinkButtonOrLink
                                    className="menu-item"
                                    data-seleniumid="menu-home"
                                    href={routes.home}
                                    role="menuitem"
                                >
                                    {localizer(`${ID.BUTTONS}.TOP_MENU.HOME`)}
                                </LinkButtonOrLink>

                                <LinkButtonOrLink
                                    className="menu-item"
                                    data-seleniumid="menu-dashboard"
                                    href={dashboardURL}
                                    role="menuitem"
                                >
                                    {localizer(`${ID.BUTTONS}.TOP_MENU.DASHBOARD`)}
                                </LinkButtonOrLink>

                                <ResourcesMenu
                                    isOpen={isResourcesOpen}
                                    onClick={this.showResourcesMenu}
                                    onHide={this.hideMenu}
                                />

                                <LinkButtonOrLink
                                    className="menu-item"
                                    data-seleniumid="menu-find-a-journal"
                                    href={localizer(`${ID.BUTTONS}.TOP_MENU.FIND_URL`)}
                                    role="menuitem"
                                >
                                    {localizer(`${ID.BUTTONS}.TOP_MENU.FIND`)}
                                </LinkButtonOrLink>
                            </div>
                        </div>
                    )}
                </div>
            </ProfileUrlProvider>
        );
    }
}

export { Menu };

export default compose(withAuth, withCodes2(ID.BUTTONS), withResponsive)(Menu);
